import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { CircularProgress } from '@mui/material';
import { ConfirmationModal } from '../../Atoms';
import { removeRpmAccount, validateRpmAccountDeletable } from '../../../store/RpmAccount';
import { useSnackbar } from '../../../Context/SnackbarContext';

export interface DeleteRpmAccountBtnProps {
  accountId: string;
  onDelete?: () => void;
}

export const DeleteRpmAccountBtn = ({ accountId, onDelete }: DeleteRpmAccountBtnProps) => {
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('Confirmation');
  const [deleteValid, setDeleteValid] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { setSnack } = useSnackbar();
  const navigate = useNavigate();

  const handleOpenModal = async () => {
    setIsValidating(true);
    const isDeletable = await validateRpmAccountDeletable(accountId);
    setIsValidating(false);
    if (typeof isDeletable === 'string') {
      setDeleteValid(false);
      setErrorMessage(isDeletable);
      setTitle('Cannot Delete');
    } else if (isDeletable === false) {
      setDeleteValid(false);
      setErrorMessage('This RPM Account cannot be deleted');
      setTitle('Cannot Delete');
    } else {
      setDeleteValid(true);
      setErrorMessage(undefined);
    }
    setOpenConfirmation(true);
  };

  const handleRemove = async () => {
    setIsDeleting(true);
    const success = await removeRpmAccount(accountId);
    setIsDeleting(false);
    if (success) {
      setSnack({ message: 'RPM Account has been deleted', type: 'success', open: true });
      onDelete?.();
      navigate('/rpm-account');
    }
  };

  return (
    <div className="px-6 py-2">
      <div className="w-full border-t border-grey-2 my-3" />
      <button
        className="text-sm text-red rounded my-2 py-2 px-4 bg-[#FFEFEF] hover:bg-[#FDD3D3]"
        onClick={handleOpenModal}>
        <DeleteIcon
          color="red"
          className="mr-2 -mt-1"
          fontSize="small"
        />
        <span>
          {isValidating ? (
            <CircularProgress
              size={14}
              color="inherit"
            />
          ) : (
            'Delete RPM Account'
          )}
        </span>
      </button>
      <ConfirmationModal
        open={openConfirmation}
        title={title}
        setOpen={setOpenConfirmation}
        handleSubmit={handleRemove}
        text={deleteValid ? 'Are you sure you want to permanently delete this RPM Account?' : ''}
        confirmButtonText={isDeleting ? 'Deleting Account...' : 'Delete'}
        disabled={!deleteValid}
        warningText={errorMessage}
      />
    </div>
  );
};
